import * as T from './types/client.types';

import EventEmitter from 'events';

import { APP_DEFAULTS, EVENTS, ERRORS } from './settings';
import { installWorker, ready as swReady, removeSubscription } from './sw';
import { createNotify } from './sync';
import { appStore } from './store';
import { checkNotificationPermission } from './utils/permission';

import { FirebaseMessagingAPI } from './client/PushAPI';
import { SubscriptionService } from './client/SubscriptionService';

const emitter = new EventEmitter();
const subscriptionService = new SubscriptionService();

appStore.subscribe((state) => {
  emitter.emit(EVENTS.CHANGE, state);
});

function getInstance(settings: T.TNotifyServerSettings): T.TReadyInstance {
  const notify = createNotify(settings);

  const instance: T.TReadyInstance = {
    getState: appStore.getState,
    toggle: () => {
      const { isSubscribed } = appStore.getState();
      return isSubscribed
        ? subscriptionService
            .unsubscribe({ notify, emitter })
            .then(() => instance)
        : subscriptionService
            .subscribe({ notify, emitter })
            .then(() => instance);
    },
    subscribe: () =>
      subscriptionService.subscribe({ notify, emitter }).then(() => instance),
    unsubscribe: () =>
      subscriptionService.unsubscribe({ notify, emitter }).then(() => instance),
  };

  return instance;
}

function handleEventRady(state: T.IState) {
  return appStore
    .dispatch({
      type: EVENTS.READY,
      state: state,
    })
    .then((state) => {
      if (state.settings) {
        emitter.emit(EVENTS.READY, getInstance(state.settings), state);
      }
    });
}

export function init(opts: Partial<T.IAppDefaults>) {
  const settings = {
    ...APP_DEFAULTS,
    ...(opts || {}),
  };

  const notify = createNotify(settings);

  // Добавляем applicationName для initializeApp (пакет firebase/app)
  subscriptionService.applicationName =
    opts.application || APP_DEFAULTS.application;

  appStore.dispatch({
    type: EVENTS.CHANGE,
    state: { settings },
  });

  const isReady: Promise<T.TReadyInstance> = new Promise((resolve) => {
    FirebaseMessagingAPI.isSupported()
      .then(checkNotificationPermission)
      .then(() => installWorker(settings))
      .then(swReady)
      .then(() => {
        return (
          subscriptionService
            // При загрузки страницы, проверяем статус подписки
            .getOrCreateSubscriptionApiFCM()
            .then((subscription) => {
              return notify(0, subscription)
                .catch((err) => {
                  emitter.emit(EVENTS.ERROR, err);
                })
                .then(() => {
                  const state = subscription
                    ? {
                        isSubscribed: true,
                        //Payload для subscription
                        subscription,
                      }
                    : { isSubscribed: false };

                  return handleEventRady(state);
                });
            })
            .catch((error) => {
              if (error?.message === ERRORS.FCM_API_INVALID_KEY.message) {
                /**
                 * Если ключ не валидный, то отписываемся от старой подписки
                 */
                return swReady()
                  .then(removeSubscription)
                  .catch(() => handleEventRady({ isSubscribed: false }))
                  .then((oldSubscription) => {
                    if (oldSubscription) {
                      return notify(-1, oldSubscription)
                        .catch((err) => {
                          emitter.emit(EVENTS.ERROR, err);
                        })
                        .then(() => handleEventRady({ isSubscribed: false }));
                    }
                  });
              }
              return handleEventRady({ isSubscribed: false });
            })
        );
      })
      .catch((err) => {
        emitter.emit(EVENTS.ERROR, err);
      })
      .then(() => {
        resolve(getInstance(settings));
      });
  });

  const instance: T.IInstance = {
    on: (eventName, listener) => {
      emitter.on(eventName, listener);
      return instance;
    },
    off: (eventName, listener) => {
      emitter.off(eventName, listener);
      return instance;
    },
    removeAllListeners: (eventName) => {
      emitter.removeAllListeners(eventName);
      return instance;
    },
    ready: () => isReady,
  };

  return instance;
}

export default init;
